import { getServerUri } from "./uriUtility";

/**
 * Reports how long the dashboard is actually in use, for the usage analytics.
 *
 * Time counts only while the tab is visible AND the person has touched the
 * mouse/keyboard in the last 5 minutes — an open but forgotten tab adds nothing.
 * The total is sent every 2 minutes (and when the tab is hidden or closed) as a
 * fire-and-forget beacon: nothing waits for it, nothing retries, and a failed
 * send is simply lost. Input listeners only store a timestamp, so they cost
 * nothing on mousemove.
 *
 * A session is per browser tab and ends after 30 minutes without activity; the
 * next activity starts a new one.
 */

const TICK_MS = 15 * 1000;
const SEND_EVERY_MS = 2 * 60 * 1000;
const IDLE_AFTER_MS = 5 * 60 * 1000;
const NEW_SESSION_AFTER_MS = 30 * 60 * 1000;
const SESSION_KEY = "usage.session";
const INPUT_EVENTS = ["pointerdown", "mousemove", "keydown", "wheel", "touchstart"];

let started = false;
let lastInputAt = Date.now();
let lastTickAt = Date.now();
let lastSentAt = Date.now();
let pendingMs = 0;

function randomId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function readSession() {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || null;
  } catch (err) {
    return null;
  }
}

function currentSessionId(now) {
  let session = readSession();
  if (!session || !session.id || now - (session.lastActiveAt || 0) > NEW_SESSION_AFTER_MS) {
    session = { id: randomId(), lastActiveAt: now };
  }
  session.lastActiveAt = now;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (err) {
    // private mode / storage full: the id just won't survive a reload
  }
  return session.id;
}

function isActive(now) {
  return document.visibilityState === "visible" && now - lastInputAt < IDLE_AFTER_MS;
}

function tick() {
  const now = Date.now();
  // Capped so a laptop waking from sleep does not count the time it slept.
  const elapsed = Math.min(now - lastTickAt, TICK_MS * 2);
  lastTickAt = now;
  if (isActive(now)) pendingMs += elapsed;
  if (now - lastSentAt >= SEND_EVERY_MS) send();
}

function send() {
  const now = Date.now();
  lastSentAt = now;
  const seconds = Math.round(pendingMs / 1000);
  if (seconds < 1) return;

  const accessToken = localStorage.getItem("session.accessToken");
  if (!accessToken) return;

  pendingMs = 0;
  const body = new URLSearchParams({ accessToken, sessionId: currentSessionId(now), seconds: String(seconds) });
  const url = getServerUri("usage/heartbeat");
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
    fetch(url, { method: "POST", body, keepalive: true }).catch(() => {});
  } catch (err) {
    // analytics must never surface an error to the person using the dashboard
  }
}

function onInput() {
  lastInputAt = Date.now();
}

function onVisibilityChange() {
  if (document.visibilityState === "hidden") {
    tick();
    send();
  } else {
    lastTickAt = Date.now();
  }
}

export function startUsageHeartbeat() {
  if (started || typeof window === "undefined") return;
  started = true;
  lastInputAt = lastTickAt = lastSentAt = Date.now();
  INPUT_EVENTS.forEach((type) => window.addEventListener(type, onInput, { passive: true, capture: true }));
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", () => {
    tick();
    send();
  });
  window.setInterval(tick, TICK_MS);
}
